import { useState, useEffect, useCallback, useRef } from 'react';
import { readScreenCache, writeScreenCache, fetchScreenData } from '../lib/screenDataCache';

/**
 * useScreenData — datos de pantalla con la regla de frescura de F4.3b.
 *
 * const { data, loading, error, setData, refetch } = useScreenData(key, fetcher, { enabled });
 *
 * - Cache FRESCO al montar → `data` sale pintable en el primer render y NO se
 *   fetchea (la pantalla real aparece al instante).
 * - Cache viejo/vacío → `loading=true` desde el primer render (la pantalla
 *   muestra su SKELETON) y al resolver el fetch llega `data`. El dato viejo
 *   jamás se pinta: stale-then-swap prohibido.
 * - `setData(next | updater)` → mutaciones del propio usuario, write-through al
 *   cache (borrar una fila, regenerar flashcards…).
 * - `refetch()` → re-fetch explícito (respuesta a un evento/hecho, p. ej.
 *   doc-listo en T4 — nunca especulativo).
 * - `enabled:false` → no fetchea (guests en Settings). Sin dato = loading queda
 *   en false para no dejar un skeleton eterno.
 * - Semilla con ORIGEN DE BODEGA (`foto`, TANDA 19 / #211) → pinta Y pregunta.
 *   Es la ÚNICA semilla que no suprime el fetch de entrada; ver abajo.
 *
 * El fetcher se guarda en un ref: cambiar su identidad entre renders NO
 * re-dispara fetches (los re-fetches solo ocurren por key nueva o refetch()).
 */
export function useScreenData(key, fetcher, { enabled = true } = {}) {
  // Snapshot del cache UNA vez por montaje (lazy init): decide skeleton vs pintado
  // instantáneo. Lecturas posteriores llegan por fetch/setData, no por re-leer.
  const [seed] = useState(() => readScreenCache(key));
  const seedFresh = Boolean(seed?.fresh);
  /* TANDA 19 (#211) — LA SEMILLA VINO DE LA BODEGA, NO DE UN FETCH.
     Hasta aquí `seedFresh` gobernaba DOS decisiones pegadas: si se pinta y si se
     pregunta. Estaban pegadas con razón —una semilla de fetch vivo dentro de su
     ventana no pudo cambiar sin que lo sepamos, así que volver a preguntar era
     gasto puro—, pero esa razón NO aplica a una foto de bodega: la bodega es lo
     que este teléfono recuerda, no lo que el servidor dice ahora.
     Esta marca las separa, y sólo para ella. Todo lo demás —las cinco pantallas
     que no hidratan, la regla de 30 s, la excepción PEND-1 D1— llega aquí con
     `foto` en false y sale por el mismo camino de siempre, línea por línea. */
  const seedFoto = Boolean(seed?.foto);

  const [data, setDataState] = useState(seedFresh ? seed.data : undefined);
  const [loading, setLoading] = useState(enabled && !seedFresh);
  const [error, setError] = useState(null);

  const fetcherRef = useRef(fetcher);
  useEffect(() => { fetcherRef.current = fetcher; }, [fetcher]);

  const refetch = useCallback(async () => {
    try {
      const next = await fetchScreenData(key, () => fetcherRef.current());
      setDataState(next);
      setError(null);
      return next;
    } catch (e) {
      setError(e);
      return null;
    } finally {
      setLoading(false);
    }
  }, [key]);

  useEffect(() => {
    if (!enabled) { setLoading(false); return; }
    // regla F4.3b: fresco → cero fetch al entrar. La foto de bodega es la única
    // semilla exenta: se pintó arriba y aquí SÍ sale a preguntar, para que el
    // dato del servidor la reemplace en su lugar (TANDA 19 / #211).
    if (seedFresh && !seedFoto) return;
    let cancelled = false;
    (async () => {
      try {
        const next = await fetchScreenData(key, () => fetcherRef.current());
        if (!cancelled) { setDataState(next); setError(null); }
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // `seedFoto` sale del MISMO snapshot congelado que `seedFresh`: es constante
    // por montaje, así que va en las deps por honestidad y no puede re-disparar.
  }, [key, enabled, seedFresh, seedFoto]);

  const setData = useCallback((updater) => {
    setDataState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      writeScreenCache(key, next);
      return next;
    });
  }, [key]);

  return { data, loading, error, setData, refetch };
}
